import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Smartphone, Loader2, CheckCircle, Copy, Check, Link2 } from 'lucide-react';
import { AppleIcon } from './constants';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WalletTab = ({ formData, id, walletTemplates }) => {
  const [googleSaving, setGoogleSaving] = React.useState(false);
  const [googleMsg, setGoogleMsg] = React.useState('');
  const [copied, setCopied] = useState(false);

  const joinUrl = `${API_URL}/join/${id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleEnsureGoogleClass = async () => {
    setGoogleSaving(true);
    setGoogleMsg('');
    try {
      const { data } = await axios.get(
        `${API_URL}/api/wallet/google/template/${id}`,
        { withCredentials: true }
      );
      if (data.configured) {
        setGoogleMsg('Classe Google Wallet créée/mise à jour avec succès');
      } else {
        setGoogleMsg('Google Wallet non configuré sur le serveur');
      }
    } catch (err) {
      setGoogleMsg('Erreur : ' + (err.response?.data?.detail || err.message));
    } finally {
      setGoogleSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Status banners (point 4) */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${
          walletTemplates?.apple?.configured
            ? 'bg-green-50 border-green-200'
            : 'bg-yellow-50 border-yellow-200'
        }`} data-testid="apple-wallet-status">
          <CheckCircle className={`w-5 h-5 flex-shrink-0 ${walletTemplates?.apple?.configured ? 'text-green-600' : 'text-yellow-600'}`} />
          <div>
            <p className={`text-sm font-medium ${walletTemplates?.apple?.configured ? 'text-green-800' : 'text-yellow-800'}`}>
              {walletTemplates?.apple?.configured
                ? 'Apple Wallet configuré et prêt par LoyalPass'
                : 'Apple Wallet en attente de configuration'}
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${
          walletTemplates?.google?.configured
            ? 'bg-green-50 border-green-200'
            : 'bg-yellow-50 border-yellow-200'
        }`} data-testid="google-wallet-status">
          <CheckCircle className={`w-5 h-5 flex-shrink-0 ${walletTemplates?.google?.configured ? 'text-green-600' : 'text-yellow-600'}`} />
          <div>
            <p className={`text-sm font-medium ${walletTemplates?.google?.configured ? 'text-green-800' : 'text-yellow-800'}`}>
              {walletTemplates?.google?.configured
                ? 'Google Wallet configuré et prêt par LoyalPass'
                : 'Google Wallet en attente de configuration'}
            </p>
          </div>
        </div>
      </div>

      {/* Join link */}
      <Card className="border-slate-200" data-testid="join-link-card">
        <CardContent className="p-4 flex items-center gap-3">
          <Link2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <code className="text-sm text-slate-700 truncate flex-1 bg-slate-50 px-3 py-2 rounded-lg" data-testid="join-link-url">{joinUrl}</code>
          <Button variant="outline" size="sm" className="gap-1.5 flex-shrink-0" onClick={handleCopy} data-testid="copy-join-link-btn">
            {copied ? <><Check className="w-3.5 h-3.5 text-green-600" /> <span className="text-green-600">Lien copié !</span></> : <><Copy className="w-3.5 h-3.5" /> Copier le lien</>}
          </Button>
        </CardContent>
      </Card>

      {/* Action cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Apple Wallet */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AppleIcon className="w-5 h-5" />
              <CardTitle>Apple Wallet</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Téléchargez un fichier .pkpass pour tester sur votre iPhone ou simulateur Apple Wallet.
            </p>
            <Button
              className="w-full"
              variant={walletTemplates?.apple?.configured ? "default" : "outline"}
              data-testid="download-pkpass-btn"
              onClick={async () => {
                try {
                  const response = await axios.get(
                    `${API_URL}/api/wallet/apple/pass/${id}/demo`,
                    { withCredentials: true, responseType: 'blob' }
                  );
                  const url = window.URL.createObjectURL(response.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${formData.name?.replace(/\s+/g, '_') || 'card'}.pkpass`;
                  a.click();
                } catch (err) {
                  console.error('Download failed:', err);
                }
              }}
            >
              <AppleIcon className="w-4 h-4 mr-2" />
              Télécharger un .pkpass exemple
            </Button>
          </CardContent>
        </Card>

        {/* Google Wallet */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <CardTitle>Google Wallet</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Vérifiez que la classe Google Wallet est correctement synchronisée pour cette carte.
            </p>
            {googleMsg && (
              <p className={`text-xs px-3 py-2 rounded-lg ${googleMsg.startsWith('Erreur') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                {googleMsg}
              </p>
            )}
            <Button
              className="w-full"
              variant={walletTemplates?.google?.configured ? "default" : "outline"}
              onClick={handleEnsureGoogleClass}
              disabled={googleSaving}
              data-testid="test-google-wallet-btn"
            >
              {googleSaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Smartphone className="w-4 h-4 mr-2" />
              )}
              Tester la classe Google Wallet
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
